<template>
  <div
    class="image-wrapper"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Slot for the image content -->
    <slot :image="image" />

    <Feature :featureSlug="featureNames.IMAGE_EDIT">
      <transition name="fade">
        <div
          v-if="isHovered || smAndDown"
          class="image-wrapper__overlay"
        >
          <v-btn
            v-if="!smAndDown"
            icon
            size="small"
            class="image-wrapper__button"
            data-product-fruits="content-creator-image-edit-fullscreen"
            variant="outlined"
            @click="toggleFullscreen"
          >
            <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              {{ $t('tooltips.fullscreen') }}
            </v-tooltip>
          </v-btn>

          <v-btn
            v-if="!isEditDisabled"
            icon
            size="small"
            class="image-wrapper__button"
            data-product-fruits="content-creator-image-edit-crop"
            variant="outlined"
            @click="openEditDialog"
          >
            <v-icon>mdi-crop</v-icon>
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              {{ $t('tooltips.edit-image') }}
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="canShowRevertButton"
            icon
            size="small"
            class="image-wrapper__button"
            data-product-fruits="content-creator-image-edit-revert"
            variant="outlined"
            @click="revertImage"
          >
            <v-icon>mdi-undo</v-icon>
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              {{ $t('tooltips.revert-image') }}
            </v-tooltip>
          </v-btn>
        </div>
      </transition>
    </Feature>

    <!-- Processing overlay for stock images -->
    <div
      v-if="processing && imageType === ImageType.STOCK"
      class="image-wrapper__processing-overlay"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      />
      <div class="image-wrapper__processing-text">
        {{ $t('alerts.image-edit.licensing-image') }}
      </div>
    </div>

    <!-- Fullscreen dialog -->
    <ImageFullscreenDialog
      v-model:is-visible="isFullscreen"
      :image="image"
      :is-stock-image="imageType === ImageType.STOCK && props.isLicenseRequired"
      @close="toggleFullscreen"
    >
      <template #content="{ image: dialogImage }">
        <slot
          name="fullscreen"
          :image="dialogImage"
        >
          <img
            v-if="dialogImage"
            :src="dialogImage"
            class="image-wrapper__fullscreen-image"
            alt="Fullscreen image"
          />
        </slot>
      </template>
    </ImageFullscreenDialog>

    <!-- Edit dialog -->
    <ImageEditDialog
      ref="editDialogRef"
      v-model:is-visible="editDialog"
      @update:is-visible="
        (value) => {
          if (!value) closeEditDialog()
        }
      "
      :edit-file="editFile"
      :processing="processing"
      :is-saving="isSaving"
      :dialog-width="dialogWidth"
      :crop-ratio="cropRatio"
      :auto-crop-area="autoCropArea"
      :has-errors="hasErrors"
      :is-image-to-small="isImageToSmall"
      :is-image-to-big="isImageToBig"
      :is-image-file-to-big="isImageFileToBig"
      :min-width="minWidth"
      :min-height="minHeight"
      :max-width="maxWidth"
      :max-height="maxHeight"
      :max-image-file-size="maxImageFileSize"
      :hide-aspect-ratio-controls="hideAspectRatioControls"
      @save="saveEditedImage"
      @cropper-ready="onCropperReady"
      @aspect-ratio-changed="onAspectRatioChanged"
      @cropper-interaction="hasUserInteracted = true"
    />
  </div>
</template>

<script setup lang="ts">
import { useApolloClient } from '@vue/apollo-composable'
import { computed, type ComputedRef, inject, nextTick, ref, watch } from 'vue'
import type { VueCropperMethods } from 'vue-cropperjs'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import ImageType from '@/lib/enums/ImageType'
import ProviderType from '@/lib/enums/ProviderType'
// @ts-ignore
import featureNames from '@/lib/featureNames'
import { uploadToS3 } from '@/lib/uploadToS3'
// @ts-ignore
import ratioMap from '@/modules/contentCreator/creator/lib/ratioMap.js'
import ConfirmImageEditMutation from '@/modules/contentCreator/creator/queries/ConfirmImageEdit.gql'
import EditImageMutation from '@/modules/contentCreator/creator/queries/editImage.gql'
import PrepareStockImageForEditMutation from '@/modules/contentCreator/creator/queries/prepareStockImageForEdit.gql'
import RequestImageEditUploadUrlMutation from '@/modules/contentCreator/creator/queries/RequestImageEditUploadUrl.gql'
import RevertGeneratedImageMutation from '@/modules/contentCreator/creator/queries/revertGeneratedImage.gql'
import RevertStockImageMutation from '@/modules/contentCreator/creator/queries/revertStockImage.gql'
import RevertUserImageMutation from '@/modules/contentCreator/creator/queries/revertUserImage.gql'
// @ts-ignore
import { Feature } from '@/plugins/feature-config'

import { useImageErrorHandling } from './composables/useImageErrorHandling'
import { useImageProcessing } from './composables/useImageProcessing'
import { useImageSentry } from './composables/useImageSentry'
import ImageEditDialog from './ImageEditDialog.vue'
import ImageFullscreenDialog from './ImageFullscreenDialog.vue'

// Types
type ImageTypeValue = (typeof ImageType)[keyof typeof ImageType]

interface ImageEditInput {
  imageId: string
  image: File
  imageType: ImageTypeValue
  topicId: string
  provider?: string
  externalId?: string
}

interface ImageFields {
  id: string
  url: string
  originalImageUrl: string
  history: Array<{
    id: string
    type: string
    timestamp: string
    userId: string
  }>
  companyId: string
}

interface GeneratedImage extends ImageFields {
  modelName: string
  topicId: string
  __typename: 'GeneratedImage'
}

interface UserImage extends ImageFields {
  topicId: string
  __typename: 'UserImage'
}

interface StockPhoto extends ImageFields {
  license: string
  provider: string
  externalId: string
  __typename: 'StockPhoto'
}

interface StockImage extends ImageFields {
  license: string
  provider: string
  externalId: string
  __typename: 'StockImage'
}

interface Material extends ImageFields {
  type: string
  purpose: string
  __typename: 'Material'
}

type EditImageResponse = GeneratedImage | UserImage | StockPhoto | StockImage | Material

interface EditImageMutationResponse {
  editImage: EditImageResponse
}

interface RevertStockImageMutationResponse {
  revertStockImage: StockImage
}

interface RevertGeneratedImageMutationResponse {
  revertGeneratedImage: GeneratedImage
}

interface RevertUserImageMutationResponse {
  revertUserImage: UserImage
}

interface ImageUpdateData {
  url: string
  [key: string]: unknown
}

// Props
interface Props {
  image?: string
  imageId: string
  imageType: ImageTypeValue
  provider?: string
  externalId?: string
  topicId: string
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
  cropRatio?: number
  autoCropArea?: number
  maxImageFileSize?: number
  hideAspectRatioControls?: boolean
  canRevert?: boolean
  isLocalImage?: boolean
  isLicenseRequired?: boolean
  fileId?: string
  history?: Array<{
    id: string
    type: string
    timestamp: string
    userId: string
  }>
}

const props = withDefaults(defineProps<Props>(), {
  image: '',
  provider: '',
  externalId: '',
  minWidth: 0,
  minHeight: 0,
  maxWidth: 12000,
  maxHeight: 12000,
  cropRatio: undefined,
  autoCropArea: 1, // Set back to 1 for exact dimensions
  maxImageFileSize: 12582912, // 12MB
  hideAspectRatioControls: true,
  canRevert: false,
  isLocalImage: false,
  isLicenseRequired: true,
  fileId: '',
  history: () => [],
})

// Emits
interface Emits {
  'image-updated': [data: ImageUpdateData]
  'image-reverted': [data: ImageUpdateData]
}

const emit = defineEmits<Emits>()

// Composables
const { t } = useI18n()
const { client } = useApolloClient()
const { smAndDown } = useDisplay()

// Access auth from app instance (for companyId and userId)
import { getCurrentInstance } from 'vue'
const app = getCurrentInstance()
const auth = app?.appContext.config.globalProperties.$auth

// Initialize composables
const {
  editFile,
  mimeType,
  imageWidth,
  imageHeight,
  imageFileSize,
  loadImageWithCORS,
  loadImageFallback,
  ensureImageSize,
  createFileFromCanvas,
  resetImageData,
} = useImageProcessing()

const { showSuccessMessage, showErrorMessage, showWarningMessage, handleGraphQLError } =
  useImageErrorHandling()

const { captureImageError, captureImageWarning, addBreadcrumb } = useImageSentry({
  imageId: props.imageId,
  imageType: props.imageType,
  topicId: props.topicId,
  provider: props.provider,
  externalId: props.externalId,
})

// Refs
const isHovered = ref<boolean>(false)
const isFullscreen = ref<boolean>(false)
const editDialog = ref<boolean>(false)
const processing = ref<boolean>(false)
const isSaving = ref<boolean>(false)
const dialogWidth = ref<number | undefined>(undefined)
const editDialogRef = ref<InstanceType<typeof ImageEditDialog> | null>(null)
const localStockImageId = ref<string | null>(null)
const hasUserInteracted = ref<boolean>(false)
const saveFileTooBig = ref<boolean>(false)

// Computed
const isImageToSmall = computed<boolean>(() => {
  return imageWidth.value < props.minWidth || imageHeight.value < props.minHeight
})

const isImageToBig = computed<boolean>(() => {
  return imageWidth.value > props.maxWidth || imageHeight.value > props.maxHeight
})

const isImageFileToBig = computed<boolean>(() => {
  return saveFileTooBig.value
})

const hasErrors = computed<boolean>(() => {
  return isImageFileToBig.value || isImageToSmall.value || isImageToBig.value
})

const isEditDisabled = computed<boolean>(() => {
  return props.imageType === ImageType.MATERIAL
})

// Determine if the current image *type* supports reverting OR an explicit canRevert flag was provided.
const canRevertImageType = computed<boolean>(() => {
  return (
    props.canRevert === true ||
    props.imageType === ImageType.STOCK ||
    props.imageType === ImageType.AI ||
    props.imageType === ImageType.USER
  )
})

// Whether the image has ever been edited (server-side history) or locally in this session.
const hasEditHistory = computed<boolean>(() => {
  return Array.isArray(props.history) && props.history.length > 0
})

// Show the revert button when the image supports reverting *and* it has either a server-side edit history
// or the current session marked it as locally edited.
const canShowRevertButton = computed<boolean>(() => {
  return (hasEditHistory.value || props.isLocalImage) && canRevertImageType.value
})

const cropRatio = computed<number>(() => {
  if (props.imageType === ImageType.STOCK && textKind) {
    const kind = textKind.value
    if (kind && ratioMap[kind]) {
      return ratioMap[kind]
    }
  }
  return props.cropRatio
})

// Watchers
watch(editDialog, (newVal: boolean, oldVal: boolean) => {
  if (newVal && !oldVal) {
    dialogWidth.value = undefined
  }
})

const textKind = inject<ComputedRef<string> | undefined>('textKind')

// Methods
const toggleFullscreen = (): void => {
  isFullscreen.value = !isFullscreen.value
}

const openEditDialog = async (): Promise<void> => {
  hasUserInteracted.value = false
  saveFileTooBig.value = false
  editDialog.value = true

  // If this is a stock image, handle preparation first
  if (props.imageType === ImageType.STOCK && props.externalId) {
    try {
      processing.value = true

      // Validate companyId and userId are available
      if (!auth?.user?.companyId) {
        throw new Error(
          'Company ID is required for stock image licensing. Please ensure you are logged in.'
        )
      }

      const userId = auth.user.userId || auth.user.sub?.split('|')?.[1] || auth.user.id

      if (!userId) {
        throw new Error(
          'User ID is required for stock image licensing. Please ensure you are logged in.'
        )
      }

      addBreadcrumb('Preparing stock image for editing', {
        externalId: props.externalId,
        provider: props.provider,
        topicId: props.topicId,
      })

      const { data } = await client.mutate({
        mutation: PrepareStockImageForEditMutation,
        variables: {
          input: {
            externalId: props.externalId,
            provider: props.provider || ProviderType.ADOBE_FREE,
            topicId: props.topicId,
          },
        },
      })

      if (data?.prepareStockImageForEdit) {
        const { stockImage, originalImageUrl, isAlreadyLicensed } = data.prepareStockImageForEdit

        addBreadcrumb('Stock image prepared for editing', {
          stockImageId: stockImage.id,
          originalImageUrl,
          isAlreadyLicensed,
        })

        // Store the local stock image ID for later use in editImage
        localStockImageId.value = stockImage.id

        // Load the licensed image
        await loadLicensedImage(originalImageUrl)
      } else {
        throw new Error('No data returned from prepareStockImageForEdit')
      }
    } catch (error) {
      captureImageError(error as Error, 'prepare-stock-image-for-edit', {
        externalId: props.externalId,
        provider: props.provider,
        topicId: props.topicId,
      })
      showErrorMessage(t('alerts.image-edit.stock-license-error'))
      closeEditDialog()
    } finally {
      processing.value = false
    }
  } else {
    // For non-stock images, use the original flow
    await loadOriginalImage()
  }
}

const loadOriginalImage = async (): Promise<void> => {
  if (props.image) {
    loadImageWithCORS(
      props.image,
      (dataUrl, width, height, estimatedSize, detectedMimeType) => {
        handleImageLoadSuccess(dataUrl, width, height, estimatedSize, detectedMimeType)
      },
      (shouldFallback) => {
        if (shouldFallback) {
          addBreadcrumb(
            'CORS image load failed, falling back to original image',
            {
              imageUrl: props.image,
            },
            'warning'
          )

          showWarningMessage(t('alerts.image-edit.cors-fallback'))

          loadImageFallback(
            props.image,
            (url, width, height, detectedMimeType, estimatedSize) => {
              handleImageLoadSuccess(url, width, height, estimatedSize, detectedMimeType)
              addBreadcrumb('Fallback image loaded successfully', {
                originalWidth: width,
                originalHeight: height,
                mimeType: detectedMimeType,
                estimatedFileSize: estimatedSize,
              })
            },
            () => {
              const error = new Error('Failed to load image in both CORS and fallback modes')
              captureImageError(error, 'image-load-complete-failure', {
                imageUrl: props.image,
              })
              showErrorMessage(t('alerts.image-edit.load-failed'))
              closeEditDialog()
            }
          )
        } else {
          captureImageError(
            new Error('Canvas context creation failed'),
            'canvas-context-creation',
            {
              imageWidth: imageWidth.value,
              imageHeight: imageHeight.value,
            }
          )
          showErrorMessage(t('alerts.image-edit.load-failed'))
          closeEditDialog()
        }
      }
    )
  }
}

const loadLicensedImage = async (imageUrl: string): Promise<void> => {
  loadImageWithCORS(
    imageUrl,
    (dataUrl, width, height, estimatedSize, detectedMimeType) => {
      handleImageLoadSuccess(dataUrl, width, height, estimatedSize, detectedMimeType)
    },
    (shouldFallback) => {
      if (shouldFallback) {
        addBreadcrumb(
          'CORS image load failed for licensed image, falling back',
          {
            imageUrl,
          },
          'warning'
        )

        showWarningMessage(t('alerts.image-edit.cors-fallback'))

        loadImageFallback(
          imageUrl,
          (url, width, height, detectedMimeType, estimatedSize) => {
            handleImageLoadSuccess(url, width, height, estimatedSize, detectedMimeType)
            addBreadcrumb('Fallback licensed image loaded successfully', {
              originalWidth: width,
              originalHeight: height,
              mimeType: detectedMimeType,
              estimatedFileSize: estimatedSize,
            })
          },
          () => {
            const error = new Error('Failed to load licensed image in both CORS and fallback modes')
            captureImageError(error, 'licensed-image-load-complete-failure', {
              imageUrl,
            })
            showErrorMessage(t('alerts.image-edit.load-failed'))
            closeEditDialog()
          }
        )
      } else {
        captureImageError(
          new Error('Canvas context creation failed for licensed image'),
          'licensed-image-canvas-context-creation',
          {
            imageWidth: imageWidth.value,
            imageHeight: imageHeight.value,
          }
        )
        showErrorMessage(t('alerts.image-edit.load-failed'))
        closeEditDialog()
      }
    }
  )
}

const handleImageLoadSuccess = (
  imageUrl: string,
  width: number,
  height: number,
  estimatedSize: number,
  detectedMimeType: string
): void => {
  try {
    const ratio = width / height
    imageWidth.value = width
    imageHeight.value = height
    dialogWidth.value = Math.max(600 * ratio, 600)
    editFile.value = imageUrl
    mimeType.value = detectedMimeType
    imageFileSize.value = estimatedSize
    saveFileTooBig.value = false

    nextTick(() => {
      const cropperRef = getCropperRef()
      if (cropperRef) {
        cropperRef.replace(editFile.value)
      }
    })
  } catch (error) {
    captureImageError(error as Error, 'image-load-processing', {
      imageWidth: width,
      imageHeight: height,
    })
    showErrorMessage(t('alerts.image-edit.load-failed'))
    closeEditDialog()
  }
}

const closeEditDialog = (): void => {
  isSaving.value = false
  processing.value = false
  editDialog.value = false
  saveFileTooBig.value = false

  addBreadcrumb('Closing image edit dialog', {
    wasSaving: isSaving.value,
    wasProcessing: processing.value,
  })

  setTimeout(() => {
    resetImageData()
    dialogWidth.value = undefined
    localStockImageId.value = null
    hasUserInteracted.value = false
  }, 300)
}

const getCropperRef = (): VueCropperMethods | null => {
  return editDialogRef.value?.cropperRef || null
}

const onCropperReady = (): void => {
  const cropperRef = getCropperRef()
  if (!cropperRef) {
    captureImageWarning('Cropper reference not available on ready event')
    return
  }

  try {
    const data = cropperRef.getData()
    ensureImageSize(data, cropperRef, imageHeight.value, (error, context) =>
      captureImageError(error, context, {}, 'ImageEditWrapper')
    )
  } catch (error) {
    captureImageError(error as Error, 'cropper-ready-event')
  }
}

const saveEditedImage = async (): Promise<void> => {
  const cropperRef = getCropperRef()
  if (!cropperRef) {
    const error = new Error('Cropper reference not available during save')
    captureImageError(error, 'save-cropper-reference-missing')
    showErrorMessage(t('alerts.image-edit.cropper-error'))
    return
  }

  // Validate that we have a valid image loaded
  if (!editFile.value) {
    const error = new Error('No image loaded in editor')
    captureImageError(error, 'save-no-image-loaded')
    showErrorMessage(t('alerts.image-edit.no-image-loaded'))
    return
  }

  // Skip re-upload if the user never interacted — avoids lossy re-encoding with no benefit
  if (!hasUserInteracted.value) {
    closeEditDialog()
    return
  }

  isSaving.value = true
  saveFileTooBig.value = false
  try {
    const data = cropperRef.getData()
    ensureImageSize(data, cropperRef, imageHeight.value, (error, context) =>
      captureImageError(error, context, {}, 'ImageEditWrapper')
    )

    const cropData = cropperRef.getData(true) // true = rounded integers
    const canvas = cropperRef.getCroppedCanvas({
      width: cropData.width,
      height: cropData.height,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })
    if (!canvas) {
      throw new Error('Failed to get cropped canvas')
    }

    addBreadcrumb('Canvas created successfully', {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      mimeType: mimeType.value,
    })

    const file = await createFileFromCanvas(canvas, mimeType.value, {
      maxBytes: props.maxImageFileSize,
      preferredMaxBytes: imageFileSize.value > 0 ? imageFileSize.value : undefined,
    })

    // Debug: Check if file was created successfully
    if (!file) {
      throw new Error('Failed to create file from canvas')
    }

    // Enhanced validation for file integrity
    if (file.size === 0) {
      throw new Error('Created file is empty')
    }

    // Verify file can be read
    try {
      await file.arrayBuffer()
    } catch (readError) {
      throw new Error('Created file cannot be read properly')
    }

    addBreadcrumb('File created from canvas', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    })

    if (file.size > props.maxImageFileSize) {
      saveFileTooBig.value = true
      showErrorMessage(
        t('alerts.image-upload.error-file', {
          max: props.maxImageFileSize / (1024 * 1024),
        })
      )
      return
    }

    // Validate provider for stock photos
    if (props.imageType === 'STOCK' && !props.provider) {
      throw new Error(
        t('alerts.image-upload.error-provider-required', {
          provider: props.provider || 'unknown',
        })
      )
    }

    // For stock images, use localStockImageId from prepare step, otherwise use imageId
    const imageIdToUse =
      props.imageType === 'STOCK' && localStockImageId.value
        ? localStockImageId.value
        : props.imageId

    const input: ImageEditInput = {
      imageId: imageIdToUse,
      image: file,
      imageType: props.imageType,
      topicId: props.topicId,
    }

    if (props.imageType === 'STOCK') {
      input.provider = props.provider
    }

    // Debug: Log mutation input
    addBreadcrumb('Sending editImage mutation', {
      originalImageId: props.imageId,
      localStockImageId: localStockImageId.value,
      finalImageId: imageIdToUse,
      imageType: input.imageType,
      topicId: input.topicId,
      provider: input.provider,
      hasFile: !!input.image,
      fileSize: input.image?.size,
      fileType: input.image?.type,
      fileName: input.image?.name,
      externalId: props.externalId,
      isStockImage: props.imageType === 'STOCK',
      editFileExists: !!editFile.value,
      cropperExists: !!getCropperRef(),
    })

    // Additional validation for stock images
    if (props.imageType === 'STOCK') {
      if (!input.topicId) {
        throw new Error('Topic ID is required for stock images')
      }
      if (!input.provider) {
        throw new Error('Provider is required for stock images')
      }
      if (!input.image) {
        throw new Error('Image file is required for stock images')
      }
      if (!localStockImageId.value) {
        throw new Error(
          'Stock image must be prepared before editing. Local StockImage ID is missing.'
        )
      }

      addBreadcrumb('Stock image validation passed', {
        topicId: input.topicId,
        provider: input.provider,
        hasFile: !!input.image,
        fileSize: input.image.size,
        localStockImageId: localStockImageId.value,
        externalId: props.externalId,
        imageIdUsed: imageIdToUse,
      })
    }

    if (!client) {
      throw new Error('Apollo Client not available')
    }

    if (import.meta.env.VITE_APP_STAGE !== 'local') {
      // Presigned URL flow: request upload URL → PUT to S3 → confirm
      const requestInput: Record<string, unknown> = {
        imageId: imageIdToUse,
        imageType: props.imageType,
        mimeType: mimeType.value,
        topicId: props.topicId,
      }
      if (props.imageType === 'STOCK') {
        requestInput.provider = props.provider
      }

      const urlResult = await client.mutate({
        mutation: RequestImageEditUploadUrlMutation,
        variables: { input: requestInput },
      })
      const { uploadUrl, s3Key } = urlResult.data.requestImageEditUploadUrl

      await uploadToS3(uploadUrl, file, mimeType.value)

      const confirmInput: Record<string, unknown> = {
        imageId: imageIdToUse,
        imageType: props.imageType,
        s3Key,
        mimeType: mimeType.value,
        topicId: props.topicId,
      }
      if (props.imageType === 'STOCK') {
        confirmInput.provider = props.provider
      }

      const { data: confirmData } = await client.mutate({
        mutation: ConfirmImageEditMutation,
        variables: { input: confirmInput },
      })

      if (confirmData?.confirmImageEdit) {
        emit('image-updated', {
          ...confirmData.confirmImageEdit,
          url: `${confirmData.confirmImageEdit.url}?t=${Date.now()}`,
        })
        closeEditDialog()
        showSuccessMessage()
        addBreadcrumb('Image edit completed successfully (presigned)', {
          imageId: props.imageId,
          imageType: props.imageType,
          finalUrl: confirmData.confirmImageEdit.url,
          returnType: confirmData.confirmImageEdit.__typename,
        })
      } else {
        showErrorMessage(t('alerts.image-edit.no-data-returned'))
        captureImageError(new Error('No data returned from confirmImageEdit'), 'graphql-no-data', {
          imageId: props.imageId,
          imageType: props.imageType,
        })
      }
    } else {
      // Legacy multipart upload flow (local only)
      const { data: mutationData } = await client.mutate<EditImageMutationResponse>({
        mutation: EditImageMutation,
        variables: { input },
        context: { hasUpload: true },
      })

      if (mutationData?.editImage) {
        emit('image-updated', {
          ...mutationData.editImage,
          url: `${mutationData.editImage.url}?t=${Date.now()}`,
        })
        closeEditDialog()
        showSuccessMessage()
        addBreadcrumb('Image edit completed successfully', {
          imageId: props.imageId,
          imageType: props.imageType,
          finalUrl: mutationData.editImage.url,
          returnType: mutationData.editImage.__typename,
        })
      } else {
        showErrorMessage(t('alerts.image-edit.no-data-returned'))
        captureImageError(new Error('No data returned from GraphQL mutation'), 'graphql-no-data', {
          imageId: props.imageId,
          imageType: props.imageType,
        })
      }
    }
  } catch (error) {
    const errorCode = handleGraphQLError(error as Error)

    // Special handling for GraphQL type resolution errors
    if (error instanceof Error && error.message.includes('ImageInterface')) {
      showErrorMessage('Backend configuration error. Please contact support.')
      captureImageError(error, 'graphql-type-resolution-error', {
        errorMessage: error.message,
        imageType: props.imageType,
      })
    }
    // Special handling for stock image errors
    else if (props.imageType === 'STOCK' && error instanceof Error) {
      if (error.message.includes('license') || error.message.includes('License')) {
        showErrorMessage(t('alerts.image-edit.stock-license-error'))
      } else if (error.message.includes('Failed to create file from canvas')) {
        showErrorMessage(t('alerts.image-edit.canvas-error'))
      } else if (error.message.includes('file')) {
        showErrorMessage('Failed to prepare image for upload. Please try again.')
      } else {
        showErrorMessage(t('alerts.image-edit.stock-error'))
      }
    }

    captureImageError(error as Error, 'save-edited-image', {
      imageId: props.imageId,
      imageType: props.imageType,
      errorCode,
      isStockImage: props.imageType === 'STOCK',
      hasProvider: !!props.provider,
      hasExternalId: !!props.externalId,
      hasTopicId: !!props.topicId,
    })
  } finally {
    isSaving.value = false
  }
}

// Methods for handling aspect ratio changes
const onAspectRatioChanged = (newRatio: number): void => {
  const cropperRef = getCropperRef()
  if (cropperRef) {
    cropperRef.setAspectRatio(newRatio)
  }
}

// Revert image functionality
const revertImage = async (): Promise<void> => {
  if (!canShowRevertButton.value) {
    return
  }

  // Use localStockImageId for revert if available (for stock images), otherwise use imageId
  const revertImageId =
    props.imageType === ImageType.STOCK && localStockImageId.value
      ? localStockImageId.value
      : props.imageId

  addBreadcrumb(`Reverting ${props.imageType.toLowerCase()} image`, {
    originalImageId: props.imageId,
    localStockImageId: localStockImageId.value,
    externalId: props.externalId,
    revertImageId: revertImageId,
    imageType: props.imageType,
  })

  try {
    let mutationData: any = null
    let revertedImage: any = null

    // Choose the appropriate mutation based on image type
    if (props.imageType === ImageType.STOCK) {
      const response = await client.mutate<RevertStockImageMutationResponse>({
        mutation: RevertStockImageMutation,
        variables: {
          input: {
            imageId: revertImageId,
          },
        },
      })
      mutationData = response.data
      revertedImage = mutationData?.revertStockImage
    } else if (props.imageType === ImageType.AI) {
      const response = await client.mutate<RevertGeneratedImageMutationResponse>({
        mutation: RevertGeneratedImageMutation,
        variables: {
          input: {
            imageId: revertImageId,
          },
        },
      })
      mutationData = response.data
      revertedImage = mutationData?.revertGeneratedImage
    } else if (props.imageType === ImageType.USER) {
      const response = await client.mutate<RevertUserImageMutationResponse>({
        mutation: RevertUserImageMutation,
        variables: {
          input: {
            imageId: revertImageId,
          },
        },
      })
      mutationData = response.data
      revertedImage = mutationData?.revertUserImage
    }

    if (revertedImage) {
      // Use originalImageUrl when url is null (which happens after revert)
      const imageUrl = revertedImage.url || revertedImage.originalImageUrl

      // Create a more aggressive cache-busting URL
      const cacheBustTimestamp = Date.now()
      const separator = imageUrl.includes('?') ? '&' : '?'
      const cacheBustedUrl = `${imageUrl}${separator}v=${cacheBustTimestamp}&t=${cacheBustTimestamp}`

      emit('image-reverted', {
        ...revertedImage,
        url: cacheBustedUrl,
      })

      // Clear the local stock image ID since we've reverted to original (only for stock images)
      if (props.imageType === ImageType.STOCK) {
        localStockImageId.value = null
      }

      // Force image refresh by emitting the updated URL
      nextTick(() => {
        // Emit image-updated to ensure parent component updates
        emit('image-updated', {
          ...revertedImage,
          url: cacheBustedUrl,
        })
      })

      showSuccessMessage(t('alerts.image-edit.revert-success'))

      addBreadcrumb('Image reverted successfully', {
        imageId: props.imageId,
        imageType: props.imageType,
        originalUrl: revertedImage.url,
        originalImageUrl: revertedImage.originalImageUrl,
        finalUrl: imageUrl,
        cacheBustedUrl,
      })
    } else {
      showErrorMessage(t('alerts.image-edit.revert-no-data'))
      captureImageError(new Error('No data returned from revert mutation'), 'revert-no-data', {
        imageId: props.imageId,
        imageType: props.imageType,
      })
    }
  } catch (error) {
    const errorCode = handleGraphQLError(error as Error)
    captureImageError(error as Error, `revert-${props.imageType.toLowerCase()}-image`, {
      imageId: props.imageId,
      imageType: props.imageType,
      errorCode,
    })
    showErrorMessage(t('alerts.image-edit.revert-failed'))
  }
}
</script>

<style scoped>
.image-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
}

.image-wrapper__overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
  z-index: 2;
}

.image-wrapper__button {
  background-color: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.image-wrapper__button:hover {
  background-color: rgba(255, 255, 255, 1) !important;
}

.image-wrapper__fullscreen-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.image-wrapper__status-indicators {
  position: absolute;
  bottom: 8px;
  left: 8px;
  display: flex;
  gap: 4px;
  z-index: 2;
}

.image-wrapper__status-chip {
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.image-wrapper__processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3;
}

.image-wrapper__processing-text {
  margin-top: 16px;
  font-size: 14px;
  color: #666;
  text-align: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
